import letterL from '../../assets/letters/L4.webp';
import letterE from '../../assets/letters/e.webp';
import letterA from '../../assets/letters/A2.webp';
import letterR from '../../assets/letters/R.webp';
import letterN from '../../assets/letters/n.webp';
import letterM from '../../assets/letters/m.webp';
import letterO from '../../assets/letters/O2.webp';
import letterR2 from '../../assets/letters/R4.webp';
import letterE2 from '../../assets/letters/E (1).webp';

// The assembled word, in reading order.
//   advance — how far the next letter starts from this one, as a fraction of viewport
//             width. The glyphs are drawn art of differing widths, so spacing is
//             per-letter; the `n` carries the word space after "Learn".
//   dy, tilt — where the letter comes to rest relative to the baseline. Uneven, so the
//             finished word sits like hand-placed cut-outs rather than type on a line —
//             but kept well under the letter height, or it reads as two rows.
//   in      — the slice of scroll this letter travels over. Staggered so they arrive one
//             after another instead of as a block.
const LETTERS = [
    { src: letterL, advance: 0.035, dy: -0.012, tilt: -5, in: [0.00, 0.62] },
    { src: letterE, advance: 0.036, dy: 0.014, tilt: 4, in: [0.10, 0.78] },
    { src: letterA, advance: 0.038, dy: -0.007, tilt: -3, in: [0.04, 0.70] },
    { src: letterR, advance: 0.044, dy: 0.016, tilt: 6, in: [0.16, 0.90] },
    { src: letterN, advance: 0.088, dy: -0.013, tilt: -4, in: [0.08, 0.66] },
    { src: letterM, advance: 0.050, dy: 0.010, tilt: 5, in: [0.20, 0.96] },
    { src: letterO, advance: 0.038, dy: -0.016, tilt: -6, in: [0.12, 0.74] },
    { src: letterR2, advance: 0.038, dy: 0.013, tilt: 3, in: [0.02, 0.58] },
    { src: letterE2, advance: 0.036, dy: -0.008, tilt: -4, in: [0.18, 0.86] },
];

// Where each letter waits before it is called in, as fractions of the box it scatters
// across. Hand-placed rather than generated: a real Math.random() would deal a new hand
// on every scroll event, and these need to hold still. Same order as LETTERS.
const SCATTER = [
    { x: 0.10, y: 0.17, rotate: -22 },
    { x: 0.31, y: 0.71, rotate: 34 },
    { x: 0.47, y: 0.19, rotate: -12 },
    { x: 0.66, y: 0.79, rotate: 25 },
    { x: 0.17, y: 0.47, rotate: 41 },
    { x: 0.85, y: 0.23, rotate: -33 },
    { x: 0.38, y: 0.87, rotate: 16 },
    { x: 0.91, y: 0.61, rotate: -19 },
    { x: 0.59, y: 0.41, rotate: 28 },
];

// The two placements of the finished word.
//   x, y    — where it comes to rest, as fractions of the box.
//   spread  — multiplies every advance. Sized so the word spans most of the box's width:
//             the desktop spacing on a phone would leave "Learn more" an inch wide.
//   jitter  — multiplies each letter's dy, for the same reason.
//   unitY   — what one unit of the box's height is worth in vh, for the travel
//             transform. Only a layout that travels needs it.
//   height  — the drawn letter's own size.
//   scatter — where the letters wait before they are called in. A layout without one is
//             simply the assembled word, drawn where it lands.
const LAYOUTS = {
    // Left of the pill column (which begins at 72%) and above it, so it reads as that
    // stack's heading.
    desktop: {
        x: 0.53, y: 0.26, spread: 1, jitter: 1, unitY: 100,
        height: 'clamp(2.5rem, 8vh, 5rem)',
        scatter: SCATTER,
        className: 'hidden lg:block',
    },
    // Near the top of its own band above the links: the band's depth is what holds the
    // links down the page, so the word sits high in it rather than centred. Static — a
    // phone screen has no room for the letters to scatter into. The letters are ~2.15×
    // wider relative to the screen than on a desktop, so spread matches that; jitter is
    // set against the band being shorter than a viewport.
    mobile: {
        x: 0.08, y: 0.41, spread: 2.15, jitter: 1.8,
        height: 'clamp(1.9rem, 9vw, 4.5rem)',
        className: 'lg:hidden',
    },
};

const clamp01 = (t) => Math.min(Math.max(t, 0), 1);

const lerp = (from, to, t) => from + (to - from) * t;

const easeOut = (t) => 1 - Math.pow(1 - t, 3);

// Overall progress → this letter's own 0..1, given the scroll slice it moves over.
const letterProgressIn = (progress, [start, end]) =>
    easeOut(clamp01((progress - start) / (end - start)));

const WORD_OFFSETS = LETTERS.reduce(
    (xs, letter, i) => [...xs, xs[i] + letter.advance],
    [0],
);

/**
 * The words "Learn more" as nine drawn letters. On desktop they start strewn across
 * their box and fly into place as you scroll through it; on mobile they are simply the
 * finished word. Fills whichever positioned element it is dropped into: the whole
 * section on desktop, a band above the links on mobile.
 *
 * @param {number} progress 0 = scattered, 1 = assembled. Ignored by a static layout.
 * @param {'desktop'|'mobile'} variant which placement to use; the other one is the one
 *        hidden at this width, so both can be rendered and CSS picks.
 */
export default function LearnMoreLetters({ progress = 1, variant = 'desktop' }) {
    const p = clamp01(progress);
    const layout = LAYOUTS[variant];

    return (
        <div
            className={`${layout.className} absolute inset-0 z-20 pointer-events-none overflow-hidden`}
            role="img"
            aria-label="Learn more"
        >
            {LETTERS.map((letter, i) => {
                const homeX = layout.x + WORD_OFFSETS[i] * layout.spread;
                const homeY = layout.y + letter.dy * layout.jitter;

                const start = layout.scatter?.[i];
                const t = start ? letterProgressIn(p, letter.in) : 1;

                // Travel is a transform in vw/vh off the letter's final spot, so it is
                // resolution-independent and never needs the viewport measured in JS. The
                // box is always full-width, so a fraction of it is that many vw; its
                // height is what unitY is for.
                const motion = start
                    ? `translate(${(start.x - homeX) * 100 * (1 - t)}vw, ` +
                      `${(start.y - homeY) * layout.unitY * (1 - t)}vh) ` +
                      `rotate(${lerp(start.rotate, letter.tilt, t)}deg) ` +
                      `scale(${lerp(0.8, 1, t)})`
                    : `rotate(${letter.tilt}deg)`;

                return (
                    <img
                        key={i}
                        src={letter.src}
                        alt=""
                        className="absolute w-auto"
                        style={{
                            left: `${homeX * 100}%`,
                            top: `${homeY * 100}%`,
                            height: layout.height,
                            transform: `translate(-50%, -50%) ${motion}`,
                        }}
                    />
                );
            })}
        </div>
    );
}
